import { useEffect, useRef } from "react";

import { type DispatchTableRow, rowUnread } from "./dispatchTableRow";

/** How often the title alternates while flashing. */
export const TITLE_FLASH_INTERVAL_MS = 1000;

/** The prefix a flashing tab shows on alternate ticks. */
export const TITLE_FLASH_PREFIX = "New message · ";

/** Drivers unread now that were not unread before: the signal that starts a flash. */
export function newlyUnreadIds(previous: ReadonlySet<string>, current: ReadonlySet<string>): string[] {
  return [...current].filter((id) => !previous.has(id));
}

/** The two frames a flash alternates between for a base title. */
export function titleFlashFrames(base: string): [string, string] {
  return [`${TITLE_FLASH_PREFIX}${base}`, base];
}

/** The ids of the rows currently unread. */
export function unreadIdsOf(rows: readonly DispatchTableRow[]): Set<string> {
  return new Set(rows.filter(rowUnread).map((row) => row.id));
}

/**
 * Flashes the tab title while the document is hidden and a driver visible in the current view gains unread traffic,
 * and restores it the moment the tab regains visibility.
 *
 * The base title is whatever the app had set when the flash began; a title another module sets mid-flash is adopted
 * as the new base rather than overwritten.
 */
export function useDispatchTitleFlash(rows: readonly DispatchTableRow[]): void {
  const previousUnreadRef = useRef<Set<string> | null>(null);
  const flashRef = useRef<{ base: string; timer: ReturnType<typeof setInterval>; frame: 0 | 1 } | null>(null);

  const stop = () => {
    const flash = flashRef.current;
    if (!flash) return;
    clearInterval(flash.timer);
    flashRef.current = null;
    if (document.title !== flash.base) document.title = flash.base;
  };

  const start = () => {
    if (flashRef.current) return;
    const base = document.title;
    const timer = setInterval(() => {
      const flash = flashRef.current;
      if (!flash) return;
      const [withPrefix, plain] = titleFlashFrames(flash.base);
      if (document.title !== withPrefix && document.title !== plain) flash.base = document.title;
      const frames = titleFlashFrames(flash.base);
      flash.frame = flash.frame === 0 ? 1 : 0;
      document.title = frames[flash.frame];
    }, TITLE_FLASH_INTERVAL_MS);
    flashRef.current = { base, timer, frame: 1 };
  };

  useEffect(() => {
    const current = unreadIdsOf(rows);
    const previous = previousUnreadRef.current;
    previousUnreadRef.current = current;
    if (!previous || !document.hidden) return;
    if (newlyUnreadIds(previous, current).length > 0) start();
  }, [rows]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) stop();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stop();
    };
  }, []);
}
